import styled from '@emotion/styled';
import { Button, css, IconButton } from '@mui/material';
import { EButtonSizes, EButtonSizesPX, EButtonVariants } from 'constants/Buttons';
import { colors } from 'theme/colors';

interface IStyledButton {
  variant:
  | 'text'
  | 'outlined'
  | 'contained'
  | undefined
  | EButtonVariants.outlinedIconTertiary;
  disabled: boolean;
  size?: 'small' | 'medium' | 'large' | undefined;
  width?: string;
  isLoading?: boolean;
  styleVariant?: 'success' | 'error';
}

interface IStyledIconButton {
  variant:
  | 'text'
  | 'outlined'
  | 'contained'
  | undefined
  | EButtonVariants.outlinedIconTertiary;
  tableButton: boolean;
  sizePX?: EButtonSizesPX;
  styleVariant?: 'success' | 'error';
  disabled: boolean;
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    [EButtonVariants.outlinedIconTertiary]: true;
  }
}

const getColorStyle = (style: string) => {
  switch (style) {
    case 'success':
      return {
        background: colors.success700,
        hover: colors.success300,
        focus: colors.success600,
        active: colors.success900,
      };
    case 'error':
      return {
        background: colors.error700,
        hover: colors.error300,
        focus: colors.error600,
        active: colors.error900,
      };
    default:
      return {
        background: colors.primary500,
        hover: colors.primary300,
        focus: colors.primary600,
        active: colors.primary900,
      };
  }
};

export const StyledButton = styled(Button, {
  shouldForwardProp(prop) {
    return prop !== 'isLoading' && prop !== 'styleVariant';
  },
}) <IStyledButton>`
  font-family: 'NotoSansMedium', sans-serif;
  height: 40px;
  padding: 10px 16px;
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 1.25rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background-color: ${colors.white};
  min-width: fit-content;

  ${(props: IStyledButton) =>
    props.isLoading &&
    css`
      pointer-events: none;
    `}

  ${(props: IStyledButton) =>
    props.size === EButtonSizes.small &&
    css`
      height: 32px;
    `}
  ${(props: IStyledButton) =>
    props.variant === EButtonVariants.contained &&
    css`
      background-color: ${props.styleVariant
        ? getColorStyle(props.styleVariant).background
        : colors.primary500};

      & svg {
        color: ${colors.white};
      }

      &:hover {
        background-color: ${props.styleVariant
        ? getColorStyle(props.styleVariant).hover
        : colors.primary900};
      }

      &:focus {
        background-color: ${props.styleVariant
        ? getColorStyle(props.styleVariant).focus
        : colors.primary600};
      }

      &:active {
        background-color: ${props.styleVariant
        ? getColorStyle(props.styleVariant).active
        : colors.primary700};
      }
    `}
  ${(props: IStyledButton) =>
    props.variant === EButtonVariants.contained &&
    props.disabled &&
    css`
      background-color: ${colors.gray200} !important;
      color: ${colors.gray400} !important;

      & svg {
        color: ${colors.gray400} !important;
      }
    `}

  ${(props: IStyledButton) =>
    props.variant === EButtonVariants.outlined &&
    css`
      background: ${colors.white};
      border-color: ${colors.primary500};
      color: ${colors.primary500};

      & svg {
        color: ${colors.primary500};
      }

      &:hover {
        background: ${colors.white};
        border-color: ${colors.primary900};
        color: ${colors.primary900};

        & svg {
          color: ${colors.primary900};
        }
      }

      &:focus {
        background: ${colors.white};
        border-color: ${colors.primary600};
        color: ${colors.primary600};

        & svg {
          color: ${colors.primary600};
        }
      }

      &:active {
        background: ${colors.white};
        border-color: ${colors.primary700};
        color: ${colors.primary700};

        & svg {
          color: ${colors.primary700};
        }
      }
    `}


  ${(props: IStyledButton) =>
    props.variant === EButtonVariants.outlined &&
    props.disabled &&
    css`
      border-color: ${colors.gray400} !important;
      color: ${colors.gray400} !important;

      & svg {
        color: ${colors.gray400} !important;
      }
    `}

  ${(props: IStyledButton) =>
    props.variant === EButtonVariants.text &&
    css`
      color: ${colors.primary500};

      & svg {
        color: ${colors.primary500};
      }

      &:hover {
        background-color: ${colors.primary25};
        color: ${colors.primary900};

        & svg {
          color: ${colors.primary900};
        }
      }

      &:focus {
        background-color: ${colors.primary25};
        color: ${colors.primary600};

        & svg {
          color: ${colors.primary600};
        }
      }

      &:active {
        background-color: ${colors.primary50};
        color: ${colors.primary700};

        & svg {
          color: ${colors.primary700};
        }
      }
    `}

  ${(props: IStyledButton) =>
    props.variant === EButtonVariants.text &&
    props.disabled &&
    css`
      color: ${colors.gray400} !important;
      background: ${colors.white} !important;
      & svg {
        color: ${colors.gray400} !important;
      }
    `}

  ${(props: IStyledButton) =>
    props.width === EButtonSizes.small &&
    css`
      width: ${props.width};
    `}
`;

const calculateIconSize = (sizePX: EButtonSizesPX) => {
  switch (sizePX) {
    case EButtonSizesPX.px24:
      return '14px';
    case EButtonSizesPX.px32:
      return '17px';
    case EButtonSizesPX.px40:
    case EButtonSizesPX.px48:
      return '21px';
    default:
      return '1em';
  }
};

export const StyledIconButton = styled(IconButton, {
  shouldForwardProp(prop) {
    return prop !== 'sizePX' && prop !== 'tableButton';
  },
}) <IStyledIconButton>`
border-radius: 4px;
background-color: ${colors.white};

  ${(props: IStyledIconButton) =>
    props.sizePX &&
    css`
      width: ${props.sizePX};
      height: ${props.sizePX};

      & svg {
        width: ${calculateIconSize(props.sizePX)};
      }
    `}
  ${(props: IStyledIconButton) =>
    props.variant === EButtonVariants.contained &&
    css`
      background-color: ${props.styleVariant
        ? getColorStyle(props.styleVariant).background
        : colors.primary500};

      & svg {
        color: ${colors.white};
      }

      &:hover {
        background-color: ${props.styleVariant
        ? getColorStyle(props.styleVariant).hover
        : colors.primary900};
      }

      &:focus {
        background-color: ${props.styleVariant
        ? getColorStyle(props.styleVariant).hover
        : colors.primary600};
      }

      &:active {
        background-color: ${props.styleVariant
        ? getColorStyle(props.styleVariant).active
        : colors.primary700};
      }
    `}

  ${(props: IStyledIconButton) =>
    (props.variant === EButtonVariants.outlined ||
      props.variant === EButtonVariants.outlinedIconTertiary) &&
    css`
      border: 1px solid ${colors.primary500};
      background-color: ${colors.white};
      & svg {
        color: ${colors.primary500};
      }

      &:hover {
        border: 1px solid ${colors.primary900};
        color: ${colors.primary900};
        background-color: ${colors.white};
        & svg {
          color: ${colors.primary900};
        }
      }

      &:focus {
        border: 1px solid ${colors.primary600};
        color: ${colors.primary600};
        background-color: ${colors.white};
        & svg {
          color: ${colors.primary600};
        }
      }

      &:active {
        border: 1px solid ${colors.primary700};
        color: ${colors.primary700};
        background-color: ${colors.white};
        & svg {
          color: ${colors.primary700};
        }
      }
    `}

  ${(props: IStyledIconButton) =>
    props.variant === EButtonVariants.outlined &&
    props.disabled &&
    css`
      border-color: ${colors.gray400} !important;
      color: ${colors.gray400} !important;
      background: ${colors.white} !important;

      & svg {
        color: ${colors.gray400} !important;
      }
    `}
  ${(props: IStyledIconButton) =>
    props.variant === EButtonVariants.contained &&
    props.disabled &&
    css`
      background-color: ${colors.gray200} !important;
      color: ${colors.gray400} !important;

      & svg {
        color: ${colors.gray400} !important;
      }
    `}

  ${(props: IStyledIconButton) =>
    props.variant === EButtonVariants.outlined &&
    props.tableButton &&
    css`
      border: 1px solid ${colors.primary50};
      background-color: ${colors.white};
      margin-right: 2px;

      & svg {
        color: ${colors.primary900};
      }

      &:hover {
        background-color: ${colors.white};
        border: 1px solid ${colors.primary900};

        & svg {
          color: ${colors.primary900};
        }
      }

      &:active,
      &:focus {
        background-color: ${colors.white};
        border: 1px solid ${colors.primary500};

        & svg {
          color: ${colors.primary900};
        }
      }
    `}
`;
